<template>
  <div class="chat-page-wrapper">
    <div class="chat-container">
      <!-- Sidebar -->
      <div class="chat-sidebar" :class="{'has-selection': selectedChat}">
        <div class="sidebar-header pt-2">
          <div class="sidebar-title-row mb-2">
            <div>
              <div class="sidebar-title">AI Chat Messages</div>
              <div v-if="lastUpdated" class="last-updated">
                Last updated: {{ lastUpdated }}
              </div>
            </div>
            <div class="sidebar-actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    @click="scrollToAskAI"
                    class="ask-ai-btn"
                    v-on="on"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Ask AI</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    @click="$router.push('/ai-cost-report')"
                    v-on="on"
                  >
                    <v-icon>mdi-currency-usd</v-icon>
                  </v-btn>
                </template>
                <span>Cost Report</span>
              </v-tooltip>
              <v-btn
                icon
                small
                @click="refreshData"
                :loading="loadingRefresh"
                class="refresh-btn"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>
          <v-text-field
            v-model="sidebarSearch"
            placeholder="Search conversations..."
            prepend-inner-icon="mdi-magnify"
            solo
            flat
            dense
            hide-details
            class="sidebar-search"
            @input="filterSidebar"
          ></v-text-field>
        </div>

        <div class="sidebar-list">
          <!-- AI Images featured entry -->
          <div class="ai-images-card" @click="$router.push('/ai-images')">
            <div class="ai-images-icon-wrap">
              <v-icon color="white" size="26">mdi-image-multiple</v-icon>
            </div>
            <div class="ai-images-card-info">
              <div class="ai-images-card-title">AI Images</div>
              <div class="ai-images-card-sub">Browse your generated images</div>
            </div>
            <v-icon color="#9e9e9e" size="18">mdi-chevron-right</v-icon>
          </div>

          <div
            v-for="chat in filteredChats"
            :key="chat.callerId"
            class="chat-item"
            :class="{
              active: selectedChat && selectedChat.callerId === chat.callerId,
            }"
            @click="selectChat(chat)"
          >
            <div
              class="chat-avatar"
              :style="{ backgroundColor: getUserColor(chat.callerId) }"
            >
              <span class="avatar-letter">{{
                getUserInitial(chat.name || chat.callerId)
              }}</span>
            </div>
            <div class="chat-info">
              <div class="chat-top">
                <div class="chat-name">{{ chat.name || chat.callerId }}</div>
                <div class="chat-date">{{ formatDate(chat.date) }}</div>
              </div>
              <div class="chat-preview">
                <v-icon v-if="chat.isImage" small class="mr-1"
                  >mdi-camera</v-icon
                >
                <v-icon
                  v-if="chat.status === 'ERROR'"
                  small
                  color="error"
                  class="mr-1"
                  >mdi-alert-circle</v-icon
                >
                <v-icon
                  v-if="chat.status === 'PENDING'"
                  small
                  color="warning"
                  class="mr-1"
                  >mdi-clock-outline</v-icon
                >
                <span
                  :class="{
                    'error--text': chat.status === 'ERROR',
                    'warning--text': chat.status === 'PENDING',
                    'rtl-text': isHebrewText(chat.lastMessage),
                  }"
                >
                  {{ trimText(chat.lastMessage) }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="filteredChats.length === 0" class="empty-state">
            <v-icon large color="grey">mdi-chat-outline</v-icon>
            <div class="mt-2">No conversations found</div>
          </div>
        </div>
      </div>

      <!-- Chat Area -->
      <div class="chat-area">
        <div v-if="!selectedChat" class="empty-chat">
          <v-icon size="100" color="grey lighten-2">mdi-chat-outline</v-icon>
          <div class="mt-4 grey--text text--darken-1">
            Select a conversation to view messages
          </div>
        </div>

        <template v-else>
          <!-- Chat Header -->
          <div class="chat-header">
            <div class="chat-header-info">
              <v-btn
                icon
                class="back-btn mr-1"
                @click="selectedChat = null"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <div
                class="chat-avatar-large"
                :style="{
                  backgroundColor: getUserColor(selectedChat.callerId),
                }"
              >
                <span class="avatar-letter-large">{{
                  getUserInitial(selectedChat.name || selectedChat.callerId)
                }}</span>
              </div>
              <div>
                <div class="chat-header-name">
                  {{ selectedChat.name || selectedChat.callerId }}
                </div>
                <div class="chat-header-phone">
                  {{ formatPhoneNumber(selectedChat.callerId) }}
                </div>
                <div class="chat-header-wallet">
                  <v-progress-circular
                    v-if="walletLoading"
                    indeterminate
                    size="16"
                    width="2"
                    color="primary"
                    class="mr-2"
                  ></v-progress-circular>
                  <v-icon
                    v-else
                    small
                    :color="walletIsLow ? 'error' : 'success'"
                    class="mr-1"
                    >mdi-wallet-outline</v-icon
                  >
                  <span
                    :class="{
                      'error--text': walletIsLow,
                      'success--text': !walletIsLow,
                    }"
                  >
                    {{ formatCurrency(walletBalance) }}
                  </span>
                  <span v-if="walletLastUpdated" class="chat-wallet-updated ml-2">
                    updated {{ formatDate(walletLastUpdated) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="chat-header-actions">
              <v-btn
                small
                outline
                elevation="0"
                class="chat-payments-btn mr-2"
                :disabled="!canManageWallet"
                @click="openPaymentsDialog"
              >
                <v-icon small>mdi-cash-multiple</v-icon>
                <span class="btn-label ml-1">Payments</span>
              </v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      small
                      outline
                      elevation="0"
                      class="chat-context-btn"
                      :disabled="!canManageAiProfile"
                      @click="openAiProfileDialog"
                    >
                      <v-icon small>mdi-account-edit-outline</v-icon>
                      <span class="btn-label ml-1">Update Context</span>
                    </v-btn>
                  </span>
                </template>
                <span v-if="canManageAiProfile">
                  View and edit this user's AI profile context
                </span>
                <span v-else>
                  This chat has no caller/contact details available
                </span>
              </v-tooltip>
            </div>
          </div>

          <!-- Messages Area -->
          <div
            class="messages-container"
            ref="messagesContainer"
            @scroll="onScroll"
          >
            <div
              v-if="loadingMessages && messages.length === 0"
              class="loading-messages"
            >
              <v-progress-circular
                indeterminate
                color="green"
                size="50"
              ></v-progress-circular>
              <div class="mt-3">Loading messages...</div>
            </div>

            <div
              v-if="loadingMore && messages.length > 0"
              class="text-center py-3"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="30"
              ></v-progress-circular>
            </div>

            <div
              v-for="message in messages"
              :key="message.Id"
              class="message-wrapper"
            >
              <!-- Question (User side - right) -->
              <div class="message-row user-message">
                <div class="message-bubble user-bubble">
                  <v-icon
                    v-if="message.Type"
                    small
                    :color="message.Type === 'WEB' ? '#2196f3' : '#4caf50'"
                    class="message-type-icon"
                  >
                    {{
                      message.Type === "WEB" ? "mdi-web" : "mdi-message-text"
                    }}
                  </v-icon>
                  <div
                    v-if="parseUrls(message.QuestionUrls).length > 0"
                    class="message-images"
                  >
                    <div
                      v-for="(url, idx) in parseUrls(message.QuestionUrls)"
                      :key="'q-' + idx"
                      class="image-container"
                    >
                      <img
                        :src="url"
                        class="message-image"
                        @click="openImageModal(url)"
                        @load="onImageLoad"
                      />
                      <v-btn
                        icon
                        small
                        class="image-download-btn"
                        @click.stop="downloadImage(url)"
                      >
                        <v-icon small>mdi-download-circle</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <audio
                    v-if="message.QuestionRecording"
                    controls
                    :src="message.QuestionRecording"
                    class="message-audio"
                  ></audio>
                  <div
                    v-if="message.Question"
                    class="message-text"
                    :class="{ 'rtl-text': isHebrewText(message.Question) }"
                  >
                    {{ trimText(message.Question) }}
                  </div>
                  <div
                    v-if="message.QuestionWithContext"
                    class="context-btn-wrapper"
                  >
                    <v-btn
                      x-small
                      text
                      color="success"
                      @click="openContextDialog(message)"
                      class="context-btn my-0"
                    >
                      <v-icon small left>mdi-information-outline</v-icon>
                      View Context
                    </v-btn>
                  </div>
                  <div class="message-time">
                    {{ formatMessageTime(message.Date) }}
                  </div>
                </div>
              </div>

              <!-- Answer (AI side - left) -->
              <div
                class="message-row ai-message"
                v-if="
                  message.Answer ||
                    message.Urls ||
                    message.Status === 'ERROR' ||
                    message.Status === 'PENDING'
                "
              >
                <div
                  class="message-bubble ai-bubble"
                  :class="{
                    'error-bubble': message.Status === 'ERROR',
                    'pending-bubble': message.Status === 'PENDING',
                  }"
                >
                  <v-icon
                    v-if="message.Type"
                    small
                    :color="message.Type === 'WEB' ? '#2196f3' : '#4caf50'"
                    class="message-type-icon"
                  >
                    {{
                      message.Type === "WEB" ? "mdi-web" : "mdi-message-text"
                    }}
                  </v-icon>
                  <div v-if="message.Status === 'ERROR'" class="error-badge">
                    <v-icon small color="white">mdi-alert-circle</v-icon>
                    <span class="ml-1">Error</span>
                  </div>
                  <div
                    v-if="message.Status === 'PENDING'"
                    class="pending-badge"
                  >
                    <v-icon small color="white">mdi-clock-outline</v-icon>
                    <span class="ml-1">Pending</span>
                  </div>
                  <div
                    v-if="parseUrls(message.Urls).length > 0"
                    class="message-images"
                  >
                    <div
                      v-for="(url, idx) in parseUrls(message.Urls)"
                      :key="'a-' + idx"
                      class="image-container"
                    >
                      <img
                        :src="url"
                        class="message-image"
                        @click="openImageModal(url)"
                        @load="onImageLoad"
                      />
                      <v-btn
                        icon
                        small
                        class="image-download-btn"
                        @click.stop="downloadImage(url)"
                      >
                        <v-icon small>mdi-download</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div
                    v-if="message.Answer && !message.Urls"
                    class="message-text markdown-content"
                    :class="{ 'rtl-text': isHebrewText(message.Answer) }"
                    v-html="formatMarkdown(message.Answer)"
                  ></div>
                  <div
                    v-if="message.Status === 'PENDING' && !message.Answer"
                    class="message-text pending-text"
                  >
                    Waiting for response...
                  </div>
                  <div class="message-footer">
                    <div class="message-footer-left">
                      <div class="message-time">
                        {{ formatMessageTime(message.Date) }}
                      </div>
                      <div
                        v-if="
                          message.HistoryCount !== null &&
                            message.HistoryCount !== undefined
                        "
                        class="message-history-count"
                      >
                        <v-icon style="font-size:10px; color:#90a4ae;"
                          >mdi-history</v-icon
                        >
                        <span>{{ message.HistoryCount }}</span>
                      </div>
                    </div>
                    <div class="message-footer-right">
                      <div v-if="message.Provider" class="message-provider">
                        {{ message.Provider }}
                      </div>
                      <div v-if="hasMessageCost(message)" class="message-cost">
                        <template v-if="message.Type !== 'WEB'">
                          ${{ getMessageTotalCost(message).toFixed(2) }}
                        </template>
                        <span
                          v-if="hasMessageApiCost(message)"
                          class="message-cost-api"
                        >
                          <template v-if="message.Type !== 'WEB'"
                            >|
                          </template>
                          API ${{ getMessageApiCost(message).toFixed(2) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="messages.length === 0 && !loadingMessages"
              class="empty-messages"
            >
              <v-icon large color="grey">mdi-message-outline</v-icon>
              <div class="mt-2">No messages yet</div>
            </div>
          </div>

          <!-- New Message Input Area -->
          <div v-if="isCurrentUserChat" class="message-input-wrapper">
            <div class="message-input-container">
              <div
                v-if="uploadedImages.length > 0"
                class="uploaded-images-preview"
              >
                <div
                  v-for="(img, idx) in uploadedImages"
                  :key="idx"
                  class="preview-image-wrapper"
                >
                  <img :src="img.preview" class="preview-image" />
                  <v-btn
                    icon
                    x-small
                    class="remove-image-btn"
                    @click="removeImage(idx)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>

              <div class="input-box">
                <div class="input-row">
                  <v-btn
                    icon
                    class="attach-btn"
                    @click="pickImages"
                    :disabled="uploadedImages.length >= 4 || sendingMessage"
                  >
                    <v-icon>mdi-paperclip</v-icon>
                  </v-btn>
                  <input
                    type="file"
                    ref="imageFiles"
                    style="display: none"
                    accept="image/*"
                    multiple
                    @change="onImagesPicked"
                  />

                  <v-textarea
                    v-model="newQuestionText"
                    placeholder="Ask AI a question..."
                    solo
                    flat
                    hide-details
                    rows="1"
                    auto-grow
                    class="message-input"
                    @keydown.enter.exact.prevent="sendQuestion"
                    @paste="onPaste"
                    @drop="onDrop"
                    @dragover.prevent
                    @dragenter.prevent
                  ></v-textarea>

                  <v-select
                    v-model="selectedModel"
                    :items="aiModels"
                    item-text="label"
                    item-value="value"
                    solo
                    flat
                    hide-details
                    class="model-select"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="model-select-text">{{
                        item.value.includes("Flash")
                          ? "Fast"
                          : item.value.includes("3.1")
                          ? "Pro 3.1"
                          : "Pro 2.5"
                      }}</span>
                    </template>
                  </v-select>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model.number="historyCount"
                        type="number"
                        min="1"
                        max="30"
                        solo
                        flat
                        hide-details
                        class="history-count-input"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <span
                      >History count (# of past messages sent as context)</span
                    >
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="image-gen-btn"
                        :class="{ active: generateImage }"
                        @click="generateImage = !generateImage"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>mdi-image-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Generate Image</span>
                  </v-tooltip>

                  <v-btn
                    icon
                    color="success"
                    class="send-btn"
                    :loading="sendingMessage"
                    :disabled="!canSendMessage"
                    @click="sendQuestion"
                  >
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Image Modal -->
      <v-dialog v-model="imageModal" max-width="65vw" max-height="90vh">
        <v-card class="pt-4">
          <v-card-text class="pa-2 image-modal-content">
            <img :src="selectedImage" class="modal-image" />
          </v-card-text>
          <v-card-actions class="py-3">
            <v-btn color="green" text @click="downloadImage(selectedImage)">
              <v-icon left>mdi-download</v-icon>
              Download
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" outline @click="imageModal = false">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Context Modal -->
      <v-dialog v-model="contextModal" max-width="800px" scrollable>
        <v-card>
          <v-card-title class="py-0 headline green white--text">
            Question Context
            <v-spacer></v-spacer>
            <v-btn icon dark @click="contextModal = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-4 pt-3 mt-2">
            <pre class="context-text">{{ contextContent }}</pre>
          </v-card-text>
          <v-card-actions class="mb-2 pt-0">
            <v-spacer></v-spacer>
            <v-btn color="default" text @click="contextModal = false">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- AI Profile Context Modal -->
      <v-dialog v-model="aiProfileDialog" max-width="1100px" scrollable>
        <v-card>
          <v-card-title class="py-0 headline primary white--text">
            AI Profile Context
            <v-spacer></v-spacer>
            <v-btn icon dark @click="closeAiProfileDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-4 pt-2 pb-0 mt-1">
            <v-alert
              v-if="aiProfileError"
              dense
              outlined
              type="warning"
              class="mb-3"
            >
              {{ aiProfileError }}
            </v-alert>

            <div class="ai-profile-meta mb-2">
              <div>
                <strong>Contact:</strong>
                {{
                  selectedChat ? selectedChat.name || selectedChat.callerId : ""
                }}
              </div>
              <div v-if="aiProfileUpdatedAt || aiProfileUpdatedBy">
                <strong>Last updated:</strong>
                {{
                  aiProfileUpdatedAt
                    ? formatDate(aiProfileUpdatedAt)
                    : "Unknown date"
                }}
                <span v-if="aiProfileUpdatedBy">
                  by {{ aiProfileUpdatedBy }}</span
                >
              </div>
            </div>
            <div class="ai-profile-meta-divider"></div>

            <div class="ai-profile-columns">
              <div class="ai-profile-column">
                <div class="ai-profile-preview-title">Editor</div>
                <v-textarea
                  v-model="aiProfileText"
                  outline
                  auto-grow
                  rows="16"
                  no-resize
                  class="ai-profile-editor"
                  :loading="aiProfileLoading"
                  placeholder="Add profile context used when asking AI questions for this user..."
                ></v-textarea>
                <div class="ai-profile-hint mb-1">
                  Line breaks are preserved. Use blank lines to separate notes.
                </div>
              </div>
              <div class="ai-profile-column">
                <div class="ai-profile-preview-title">Preview</div>
                <pre class="ai-profile-preview">{{ aiProfilePreview }}</pre>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="px-0 pb-3 pt-3">
            <v-btn
              text
              color="warning"
              :disabled="aiProfileSaving || aiProfileLoading"
              @click="clearAiProfileText"
            >
              Clear
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              outline
              class="ai-profile-cancel-btn"
              @click="closeAiProfileDialog"
            >
              Cancel
            </v-btn>
            <v-btn
              color="success"
              :loading="aiProfileSaving"
              :disabled="!canManageAiProfile || aiProfileLoading"
              @click="saveAiProfileContext"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Wallet Payments Modal -->
      <v-dialog v-model="paymentsDialog" max-width="900px" scrollable>
        <v-card>
          <v-card-title class="py-0 headline indigo white--text">
            Wallet Payments
            <v-spacer></v-spacer>
            <v-btn icon dark @click="paymentsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-4 pt-3 pb-1 mt-1">
            <div class="wallet-summary-row mb-3">
              <div>
                <strong>User:</strong>
                {{
                  selectedChat ? selectedChat.name || selectedChat.callerId : ""
                }}
              </div>
              <div>
                <strong>Balance:</strong>
                <span
                  :class="{
                    'error--text': walletIsLow,
                    'success--text': !walletIsLow,
                  }"
                >
                  {{ formatCurrency(walletBalance) }}
                </span>
              </div>
            </div>

            <v-alert
              v-if="paymentsError"
              dense
              outlined
              type="error"
              class="mb-3"
            >
              {{ paymentsError }}
            </v-alert>

            <div class="payment-form-grid mb-2">
              <v-text-field
                v-model.number="paymentAmount"
                type="number"
                min="0.01"
                step="0.01"
                label="Amount"
                prefix="$"
                dense
                outlined
                hide-details
              ></v-text-field>
              <v-text-field
                v-model="paymentMethod"
                label="Payment Method"
                placeholder="CASH / CARD / ZELLE"
                dense
                outlined
                hide-details
              ></v-text-field>
              <v-btn
                color="success"
                :loading="addingPayment"
                :disabled="!canSubmitPayment"
                @click="addPayment"
              >
                Add Payment
              </v-btn>
            </div>

            <v-textarea
              v-model="paymentNotes"
              label="Notes"
              rows="2"
              auto-grow
              dense
              outlined
              class="mb-2"
            ></v-textarea>

            <div class="payment-logs-title mb-2">
              Payment History
              <v-btn
                x-small
                text
                color="primary"
                :loading="paymentsLoading"
                @click="loadPaymentLogs"
              >
                Refresh
              </v-btn>
            </div>

            <div
              v-if="paymentsLoading && paymentLogs.length === 0"
              class="text-center py-6"
            >
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>

            <div v-else class="payment-logs-list">
              <div v-if="paymentLogs.length === 0" class="grey--text">
                No payments found for this user.
              </div>
              <div
                v-for="(log, idx) in paymentLogs"
                :key="log.id || log.Id || idx"
                class="payment-log-row"
              >
                <div class="payment-log-amount success--text">
                  +{{ formatCurrency(log.amount || log.Amount) }}
                </div>
                <div class="payment-log-details">
                  <div>
                    {{ log.paymentMethod || log.PaymentMethod || "Payment" }}
                  </div>
                  <div class="payment-log-notes">
                    {{ log.notes || log.Notes || "No notes" }}
                  </div>
                </div>
                <div class="payment-log-date">
                  {{
                    formatMessageTime(
                      log.dateCreated ||
                        log.DateCreated ||
                        log.date ||
                        log.Date
                    )
                  }}
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="pb-3">
            <v-spacer></v-spacer>
            <v-btn outline @click="paymentsDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import * as markedLib from "marked";

export default {
  data() {
    return {
      chats: [],
      filteredChats: [],
      sidebarSearch: "",
      selectedChat: null,
      messages: [],
      loadingMessages: false,
      loadingMore: false,
      loadingRefresh: false,
      lastUpdated: null,

      // Pagination
      currentSkip: 0,
      pageSize: 20,
      hasMoreMessages: true,

      // Image modal
      imageModal: false,
      selectedImage: null,

      // Context modal
      contextModal: false,
      contextContent: "",
      aiProfileDialog: false,
      aiProfileLoading: false,
      aiProfileSaving: false,
      aiProfileError: "",
      aiProfileText: "",
      aiProfileUpdatedBy: "",
      aiProfileUpdatedAt: null,
      walletLoading: false,
      walletBalance: 0,
      walletIsLow: false,
      walletLastUpdated: null,
      paymentsDialog: false,
      paymentsLoading: false,
      paymentsError: "",
      paymentLogs: [],
      paymentAmount: null,
      paymentMethod: "CASH",
      paymentNotes: "",
      addingPayment: false,

      // New message input
      newQuestionText: "",
      uploadedImages: [],
      generateImage: false,
      selectedModel: localStorage.getItem("aiModel") || "Gemini 2.5 Pro",
      historyCount: Math.min(
        30,
        Math.max(1, parseInt(localStorage.getItem("historyCount") || "10", 10))
      ),
      aiModels: [
        {
          label: "Gemini 3 Flash Preview (Faster)",
          value: "Gemini 3 Flash Preview",
        },
        {
          label: "Gemini 3.1 Pro Preview",
          value: "Gemini 3.1 Pro Preview",
        },
        {
          label: "Gemini 2.5 Pro",
          value: "Gemini 2.5 Pro",
        },
      ],
      sendingMessage: false,

      // Idle timer
      idleTimer: null,
      idleTimeout: 10 * 60 * 1000, // 10 minutes in milliseconds
    };
  },

  created() {
    this.loadSidebar();
  },

  mounted() {
    this.setupIdleTimer();
    this.$nextTick(() => {
      this.addActivityListeners();
    });
  },

  beforeDestroy() {
    this.removeActivityListeners();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
  },

  methods: {
    async loadSidebar() {
      try {
        const response = await this.$http.get(
          this.getDomain() + "/api/openai/getChatSidebar"
        );
        this.chats = response.data;
        this.filteredChats = this.chats;
        this.lastUpdated = moment().format("MMM D, h:mm A");
      } catch (error) {
        console.error("Error loading sidebar:", error);
      }
    },

    filterSidebar() {
      const search = this.sidebarSearch.toLowerCase();
      this.filteredChats = this.chats.filter(
        (chat) =>
          (chat.name && chat.name.toLowerCase().includes(search)) ||
          (chat.callerId && chat.callerId.includes(search)) ||
          (chat.lastMessage && chat.lastMessage.toLowerCase().includes(search))
      );
    },

    async selectChat(chat) {
      this.selectedChat = chat;
      this.messages = [];
      this.currentSkip = 0;
      this.hasMoreMessages = true;
      this.applyWalletFromChatRow(chat);
      this.loadWalletBalance();
      await this.loadMessages(true);
    },

    async loadMessages(scrollToBottom = false) {
      if (!this.selectedChat || this.loadingMessages) return;

      this.loadingMessages = true;

      try {
        const url =
          this.getDomain() +
          `/odata/aiQuestions?$filter=CallerId eq '${
            this.selectedChat.callerId
          }'` +
          `&$orderby=Date desc&$top=${this.pageSize}&$skip=${
            this.currentSkip
          }` +
          `&$count=true`;

        const response = await this.$http.get(url);

        const newMessages = response.data.value;

        if (newMessages.length < this.pageSize) {
          this.hasMoreMessages = false;
        }

        // Add new messages to the beginning (since we're loading older messages)
        this.messages = [...newMessages.reverse(), ...this.messages];
        this.currentSkip += newMessages.length;

        if (scrollToBottom) {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("Error loading messages:", error);
      } finally {
        this.loadingMessages = false;
        this.loadingMore = false;
      }
    },

    async onScroll(event) {
      const container = event.target;

      // Check if scrolled to top (with small threshold)
      if (
        container.scrollTop < 100 &&
        !this.loadingMore &&
        this.hasMoreMessages
      ) {
        const oldScrollHeight = container.scrollHeight;
        this.loadingMore = true;

        await this.loadMessages(false);

        // Maintain scroll position
        this.$nextTick(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - oldScrollHeight;
        });
      }
    },

    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        setTimeout(() => {
          this.$nextTick(() => {
            const container = this.$refs.messagesContainer;
            container.scrollTop = container.scrollHeight + 1000;
          });
        }, 150);
        // Additional scroll after images have had time to load
        setTimeout(() => {
          this.$nextTick(() => {
            const container = this.$refs.messagesContainer;
            container.scrollTop = container.scrollHeight + 1000;
          });
        }, 500);
      }
    },

    parseUrls(urlString) {
      if (!urlString) return [];
      // Split by comma and filter out empty/whitespace-only entries
      const urls = urlString
        .split(",")
        .map((url) => url.trim())
        .filter((url) => url.length > 0);
      console.log("Parsed URLs:", urls); // Debug log
      return urls;
    },

    trimText(text) {
      if (!text) return text;
      // Only remove leading and trailing whitespace/newlines
      return text.trimStart().trimEnd();
    },

    formatMarkdown(text) {
      if (!text) return "";

      try {
        // Use marked to render the markdown
        const html = markedLib.marked(text, {
          breaks: true,
          gfm: true,
          headerIds: false,
          mangle: false,
          smartLists: true,
          smartypants: true,
        });

        return html;
      } catch (error) {
        console.error("Error rendering markdown:", error);
        // Fallback to plain text with line breaks
        return text.replace(/\n/g, "<br>");
      }
    },

    isHebrewText(text) {
      if (!text) return false;
      // Hebrew Unicode range: \u0590-\u05FF
      const hebrewRegex = /[\u0590-\u05FF]/;
      return hebrewRegex.test(text);
    },

    getUserInitial(name) {
      if (!name) return "?";
      // Get first character, handling Hebrew or English
      const firstChar = name.trim()[0];
      return firstChar ? firstChar.toUpperCase() : "?";
    },

    getUserColor(callerId) {
      // Generate consistent color based on caller ID
      const colors = [
        "#e91e63", // pink
        "#9c27b0", // purple
        "#673ab7", // deep purple
        "#3f51b5", // indigo
        "#2196f3", // blue
        "#00bcd4", // cyan
        "#009688", // teal
        "#4caf50", // green
        "#ff9800", // orange
        "#ff5722", // deep orange
        "#795548", // brown
        "#607d8b", // blue grey
      ];

      if (!callerId) return colors[0];

      // Simple hash function
      let hash = 0;
      for (let i = 0; i < callerId.length; i++) {
        hash = callerId.charCodeAt(i) + ((hash << 5) - hash);
      }

      return colors[Math.abs(hash) % colors.length];
    },

    openImageModal(url) {
      this.selectedImage = url;
      this.imageModal = true;
    },

    openContextDialog(message) {
      this.contextContent = message.QuestionWithContext || "";
      this.contextModal = true;
    },

    getSelectedChatContextLookup() {
      if (!this.selectedChat) {
        return { userContactId: null, callerId: "" };
      }

      let userContactId = null;
      if (
        this.selectedChat.userContactId !== null &&
        this.selectedChat.userContactId !== undefined
      ) {
        userContactId = this.selectedChat.userContactId;
      } else if (
        this.selectedChat.UserContactId !== null &&
        this.selectedChat.UserContactId !== undefined
      ) {
        userContactId = this.selectedChat.UserContactId;
      } else if (
        this.selectedChat.userContact &&
        this.selectedChat.userContact.id !== null &&
        this.selectedChat.userContact.id !== undefined
      ) {
        userContactId = this.selectedChat.userContact.id;
      } else if (
        this.selectedChat.userContact &&
        this.selectedChat.userContact.Id !== null &&
        this.selectedChat.userContact.Id !== undefined
      ) {
        userContactId = this.selectedChat.userContact.Id;
      } else if (
        this.selectedChat.UserContact &&
        this.selectedChat.UserContact.id !== null &&
        this.selectedChat.UserContact.id !== undefined
      ) {
        userContactId = this.selectedChat.UserContact.id;
      } else if (
        this.selectedChat.UserContact &&
        this.selectedChat.UserContact.Id !== null &&
        this.selectedChat.UserContact.Id !== undefined
      ) {
        userContactId = this.selectedChat.UserContact.Id;
      }

      let callerId =
        this.selectedChat.callerId ||
        this.selectedChat.phoneNumber ||
        this.selectedChat.CallerId ||
        this.selectedChat.PhoneNumber ||
        "";
      callerId = callerId ? String(callerId).trim() : "";

      return { userContactId: userContactId, callerId: callerId };
    },

    resetAiProfileEditor() {
      this.aiProfileError = "";
      this.aiProfileText = "";
      this.aiProfileUpdatedBy = "";
      this.aiProfileUpdatedAt = null;
      this.aiProfileLoading = false;
      this.aiProfileSaving = false;
    },

    closeAiProfileDialog() {
      this.aiProfileDialog = false;
    },

    clearAiProfileText() {
      this.aiProfileText = "";
    },

    applyAiProfileResponse(responseData) {
      if (!responseData) {
        this.aiProfileText = "";
        this.aiProfileUpdatedBy = "";
        this.aiProfileUpdatedAt = null;
        return;
      }

      this.aiProfileText =
        responseData.context === null || responseData.context === undefined
          ? ""
          : String(responseData.context);
      this.aiProfileUpdatedBy = responseData.aiProfileUpdatedBy || "";
      this.aiProfileUpdatedAt = responseData.aiProfileUpdatedAt || null;
    },

    mergeAiProfileIntoSelectedChat(contextData) {
      if (!this.selectedChat || !contextData) return;

      this.selectedChat.aiProfile = contextData.context;
      this.selectedChat.context = contextData.context;
      this.selectedChat.aiProfileUpdatedBy = contextData.aiProfileUpdatedBy;
      this.selectedChat.aiProfileUpdatedAt = contextData.aiProfileUpdatedAt;

      for (let i = 0; i < this.chats.length; i++) {
        if (this.chats[i].callerId === this.selectedChat.callerId) {
          this.chats[i].aiProfile = contextData.context;
          this.chats[i].context = contextData.context;
          this.chats[i].aiProfileUpdatedBy = contextData.aiProfileUpdatedBy;
          this.chats[i].aiProfileUpdatedAt = contextData.aiProfileUpdatedAt;
          break;
        }
      }
    },

    getSelectedChatWalletLookup() {
      if (!this.selectedChat) {
        return { userId: null, callerId: "" };
      }

      let userId = null;
      if (this.selectedChat.userId !== null && this.selectedChat.userId !== undefined) {
        userId = this.selectedChat.userId;
      } else if (
        this.selectedChat.UserId !== null &&
        this.selectedChat.UserId !== undefined
      ) {
        userId = this.selectedChat.UserId;
      }

      let callerId =
        this.selectedChat.callerId ||
        this.selectedChat.CallerId ||
        this.selectedChat.phoneNumber ||
        this.selectedChat.PhoneNumber ||
        "";
      callerId = callerId ? String(callerId).trim() : "";

      return { userId: userId, callerId: callerId };
    },

    applyWalletFromChatRow(chat) {
      if (!chat) {
        this.walletBalance = 0;
        this.walletIsLow = false;
        this.walletLastUpdated = null;
        return;
      }

      if (chat.currentBalance !== null && chat.currentBalance !== undefined) {
        const parsed = Number(chat.currentBalance);
        this.walletBalance = Number.isFinite(parsed) ? parsed : 0;
      } else {
        this.walletBalance = 0;
      }

      this.walletIsLow = !!chat.isLowBalance;
      this.walletLastUpdated = chat.walletLastUpdated || null;
    },

    mergeWalletIntoSelectedChat(balanceData) {
      if (!this.selectedChat || !balanceData) return;

      const currentBalance =
        balanceData.currentBalance === null ||
        balanceData.currentBalance === undefined
          ? 0
          : Number(balanceData.currentBalance);
      const isLowBalance =
        balanceData.isLowBalance === null || balanceData.isLowBalance === undefined
          ? currentBalance < 10
          : !!balanceData.isLowBalance;
      const lastUpdated = balanceData.lastUpdated || null;

      this.walletBalance = Number.isFinite(currentBalance) ? currentBalance : 0;
      this.walletIsLow = isLowBalance;
      this.walletLastUpdated = lastUpdated;

      this.selectedChat.currentBalance = this.walletBalance;
      this.selectedChat.isLowBalance = this.walletIsLow;
      this.selectedChat.walletLastUpdated = this.walletLastUpdated;

      for (let i = 0; i < this.chats.length; i++) {
        if (this.chats[i].callerId === this.selectedChat.callerId) {
          this.chats[i].currentBalance = this.walletBalance;
          this.chats[i].isLowBalance = this.walletIsLow;
          this.chats[i].walletLastUpdated = this.walletLastUpdated;
          break;
        }
      }
    },

    async loadWalletBalance() {
      const lookup = this.getSelectedChatWalletLookup();
      if (
        (lookup.userId === null || lookup.userId === undefined) &&
        !lookup.callerId
      ) {
        return;
      }

      const params = {};
      if (lookup.userId !== null && lookup.userId !== undefined) {
        params.userId = lookup.userId;
      } else {
        params.callerId = lookup.callerId;
      }

      this.walletLoading = true;
      try {
        const response = await this.$http.get(
          this.getDomain() + "/api/openAi/wallet/getBalance",
          { params: params }
        );
        this.mergeWalletIntoSelectedChat(response.data);
      } catch (error) {
        console.error("Error loading wallet balance:", error);
      } finally {
        this.walletLoading = false;
      }
    },

    async loadPaymentLogs() {
      const lookup = this.getSelectedChatWalletLookup();
      if (
        (lookup.userId === null || lookup.userId === undefined) &&
        !lookup.callerId
      ) {
        this.paymentsError = "No user identifier found for this chat.";
        this.paymentLogs = [];
        return;
      }

      const params = { take: 200 };
      if (lookup.userId !== null && lookup.userId !== undefined) {
        params.userId = lookup.userId;
      } else {
        params.callerId = lookup.callerId;
      }

      this.paymentsLoading = true;
      this.paymentsError = "";
      try {
        const response = await this.$http.get(
          this.getDomain() + "/api/openAi/wallet/getAllPayments",
          { params: params }
        );
        this.paymentLogs = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error loading payment logs:", error);
        this.paymentsError = "Failed to load payment logs.";
        this.paymentLogs = [];
      } finally {
        this.paymentsLoading = false;
      }
    },

    async openPaymentsDialog() {
      if (!this.canManageWallet) return;
      this.paymentsDialog = true;
      await this.loadWalletBalance();
      await this.loadPaymentLogs();
    },

    async addPayment() {
      if (!this.canSubmitPayment) return;

      const lookup = this.getSelectedChatWalletLookup();
      const payload = {
        userId:
          lookup.userId !== null && lookup.userId !== undefined
            ? lookup.userId
            : null,
        callerId:
          lookup.userId === null || lookup.userId === undefined
            ? lookup.callerId
            : null,
        amount: Number(this.paymentAmount),
        paymentMethod: this.paymentMethod ? this.paymentMethod.trim() : "CASH",
        notes: this.paymentNotes ? this.paymentNotes.trim() : "",
      };

      this.addingPayment = true;
      this.paymentsError = "";
      try {
        const response = await this.$http.post(
          this.getDomain() + "/api/openAi/wallet/addPayment",
          payload
        );
        this.mergeWalletIntoSelectedChat(response.data);
        this.paymentAmount = null;
        this.paymentNotes = "";
        await this.loadPaymentLogs();
      } catch (error) {
        console.error("Error adding payment:", error);
        this.paymentsError = "Failed to add payment.";
      } finally {
        this.addingPayment = false;
      }
    },

    async loadAiProfileContext() {
      const lookup = this.getSelectedChatContextLookup();
      if (
        (lookup.userContactId === null || lookup.userContactId === undefined) &&
        !lookup.callerId
      ) {
        this.aiProfileError = "No contact identifier found for this chat.";
        this.aiProfileText = "";
        return;
      }

      const params = {};
      if (lookup.userContactId !== null && lookup.userContactId !== undefined) {
        params.userContactId = lookup.userContactId;
      } else if (lookup.callerId) {
        params.callerId = lookup.callerId;
      }

      this.aiProfileLoading = true;
      this.aiProfileError = "";

      try {
        const response = await this.$http.get(
          this.getDomain() + "/api/openAi/userContact/context",
          { params: params }
        );
        this.applyAiProfileResponse(response.data);
      } catch (error) {
        const status = error && error.response ? error.response.status : null;
        if (status === 404) {
          this.aiProfileText = "";
          this.aiProfileUpdatedBy = "";
          this.aiProfileUpdatedAt = null;
          this.aiProfileError =
            "No profile context exists yet for this contact.";
          return;
        }
        if (status === 400) {
          this.aiProfileError =
            "Unable to load context because no valid contact id was provided.";
          return;
        }
        console.error("Error loading AI profile context:", error);
        this.aiProfileError = "Failed to load context. Please try again.";
      } finally {
        this.aiProfileLoading = false;
      }
    },

    async openAiProfileDialog() {
      if (!this.canManageAiProfile) return;

      this.aiProfileDialog = true;
      await this.loadAiProfileContext();
    },

    async saveAiProfileContext() {
      const lookup = this.getSelectedChatContextLookup();
      if (
        (lookup.userContactId === null || lookup.userContactId === undefined) &&
        !lookup.callerId
      ) {
        this.aiProfileError = "No contact identifier found for this chat.";
        return;
      }

      const payload = {
        userContactId:
          lookup.userContactId !== null && lookup.userContactId !== undefined
            ? lookup.userContactId
            : null,
        callerId:
          lookup.userContactId === null || lookup.userContactId === undefined
            ? lookup.callerId || null
            : null,
        context: this.aiProfileText,
      };

      const updatedByFromStore =
        this.$store && this.$store.state && this.$store.state.user;

      if (updatedByFromStore) {
        payload.updatedBy = updatedByFromStore;
      }

      this.aiProfileSaving = true;
      this.aiProfileError = "";

      try {
        const response = await this.$http.post(
          this.getDomain() + "/api/openAi/userContact/context",
          payload
        );
        this.applyAiProfileResponse(response.data);
        this.mergeAiProfileIntoSelectedChat(response.data);
        this.closeAiProfileDialog();
      } catch (error) {
        const status = error && error.response ? error.response.status : null;
        if (status === 404) {
          this.aiProfileError =
            "Contact was not found. Refresh chats and try again.";
        } else if (status === 400) {
          this.aiProfileError =
            "Unable to save context. Provide a valid user/contact id.";
        } else {
          console.error("Error saving AI profile context:", error);
          this.aiProfileError = "Failed to save context. Please try again.";
        }
      } finally {
        this.aiProfileSaving = false;
      }
    },

    downloadImage(url) {
      if (!url) return;
      const link = document.createElement("a");
      const fileNameFromUrl = url.split("/").pop() || "image.jpg";
      const cleanFileName = fileNameFromUrl.split("?")[0] || "image.jpg";
      const proxyUrl =
        this.getDomain() +
        "/api/openAi/downloadFile?url=" +
        encodeURIComponent(url) +
        "&fileName=" +
        encodeURIComponent(cleanFileName);
      link.href = proxyUrl;
      link.download = cleanFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    onImageLoad() {
      // When images load, scroll to bottom to ensure we're at the actual bottom
      if (this.$refs.messagesContainer) {
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer;
          const isNearBottom =
            container.scrollHeight -
              container.scrollTop -
              container.clientHeight <
            200;
          // Only auto-scroll if we're already near the bottom (user intent to see latest)
          if (isNearBottom) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
    },

    formatDate(dateStr) {
      const date = moment(dateStr);
      const now = moment();

      if (date.isSame(now, "day")) {
        return date.format("h:mm A");
      } else if (date.isSame(now.clone().subtract(1, "day"), "day")) {
        return "Yesterday";
      } else if (date.isAfter(now.clone().subtract(7, "days"))) {
        return date.format("dddd");
      } else {
        return date.format("M/D/YY");
      }
    },

    formatMessageTime(dateStr) {
      if (!dateStr) {
        return "";
      }
      const date = moment(dateStr);
      const now = moment();

      if (date.isSame(now, "day")) {
        return date.format("h:mm A");
      } else if (date.isSame(now.clone().subtract(1, "day"), "day")) {
        return "Yesterday " + date.format("h:mm A");
      } else if (date.isAfter(now.clone().subtract(7, "days"))) {
        return date.format("dddd h:mm A");
      } else {
        return date.format("MMM D, YYYY h:mm A");
      }
    },

    formatCurrency(amount) {
      const parsed = Number(amount);
      if (!Number.isFinite(parsed)) {
        return "$0.00";
      }
      return "$" + parsed.toFixed(2);
    },

    getMessageQuestionCost(message) {
      return Number(message.QuestionCost || 0);
    },

    getMessageApiCost(message) {
      if (message.ApiCost === null || message.ApiCost === undefined) {
        return null;
      }
      const apiCost = Number(message.ApiCost);
      return Number.isFinite(apiCost) ? apiCost : null;
    },

    getMessageTotalCost(message) {
      return Number(message.Cost || 0) + this.getMessageQuestionCost(message);
    },

    hasMessageCost(message) {
      return (
        this.getMessageTotalCost(message) > 0 || this.hasMessageApiCost(message)
      );
    },

    hasMessageApiCost(message) {
      return this.getMessageApiCost(message) !== null;
    },

    formatPhoneNumber(phoneNumberString) {
      if (!phoneNumberString) return "";

      if (phoneNumberString.length == 11) {
        phoneNumberString = phoneNumberString.substring(1);
      }

      var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
      var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3];
      }
      return phoneNumberString;
    },

    async refreshData() {
      this.loadingRefresh = true;
      try {
        await this.loadSidebar();
        // Clear selected chat and messages
        this.selectedChat = null;
        this.messages = [];
        this.currentSkip = 0;
        this.hasMoreMessages = true;
      } catch (error) {
        console.error("Error refreshing data:", error);
      } finally {
        this.loadingRefresh = false;
      }
    },

    setupIdleTimer() {
      // Clear existing timer
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }

      // Set new timer for 10 minutes
      this.idleTimer = setTimeout(async () => {
        console.log("Idle timeout reached - refreshing data");
        await this.refreshData();
        // Set up the next timer to continue refreshing every 10 minutes while idle
        this.setupIdleTimer();
      }, this.idleTimeout);
    },

    resetIdleTimer() {
      console.log("Activity detected - resetting idle timer");
      this.setupIdleTimer();
    },

    addActivityListeners() {
      // Listen to meaningful user interactions only (removed mousemove to avoid false positives)
      const events = ["click", "keypress", "scroll", "touchstart"];
      const container = this.$el;
      if (container) {
        console.log("Setting up activity listeners on chat container");
        events.forEach((event) => {
          container.addEventListener(event, this.resetIdleTimer, true);
        });
      } else {
        console.warn("Chat container not found for activity listeners");
      }
    },

    removeActivityListeners() {
      const events = ["click", "keypress", "scroll", "touchstart"];
      events.forEach((event) => {
        const container = this.$el;
        if (container) {
          container.removeEventListener(event, this.resetIdleTimer, true);
        }
      });
    },

    scrollToAskAI() {
      // Get current user's phone number
      const currentUserPhone = this.$store.state.phoneNumber;

      // Find if there's already a chat for current user
      const userChat = this.chats.find(
        (chat) =>
          chat.phoneNumber === currentUserPhone ||
          chat.callerId === currentUserPhone
      );

      if (userChat) {
        // Select existing chat
        this.selectChat(userChat);
      } else if (this.filteredChats.length > 0) {
        // If current user has no chat, still allow viewing other chats
        this.selectChat(this.filteredChats[0]);
      }

      // Scroll to bottom and focus textarea
      this.$nextTick(() => {
        this.scrollToBottom();
        const textarea = document.querySelector(".message-input textarea");
        if (textarea) {
          textarea.focus();
        }
      });
    },

    pickImages() {
      this.$refs.imageFiles.click();
    },

    onPaste(event) {
      const items = event.clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file && this.uploadedImages.length < 4) {
            const reader = new FileReader();
            reader.onload = (e) => {
              this.uploadedImages.push({
                file: file,
                preview: e.target.result,
              });
            };
            reader.readAsDataURL(file);
          }
        }
      }
    },

    onImagesPicked(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Calculate how many more images we can add
      const remaining = 4 - this.uploadedImages.length;
      const filesToAdd = Math.min(files.length, remaining);

      for (let i = 0; i < filesToAdd; i++) {
        const file = files[i];
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.uploadedImages.push({
              file: file,
              preview: e.target.result,
            });
          };
          reader.readAsDataURL(file);
        }
      }

      // Clear the input
      this.$refs.imageFiles.value = "";
    },

    onDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (!files || files.length === 0) return;

      // Calculate how many more images we can add
      const remaining = 4 - this.uploadedImages.length;
      const filesToAdd = Math.min(files.length, remaining);

      for (let i = 0; i < filesToAdd; i++) {
        const file = files[i];
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.uploadedImages.push({
              file: file,
              preview: e.target.result,
            });
          };
          reader.readAsDataURL(file);
        }
      }
    },

    removeImage(index) {
      this.uploadedImages.splice(index, 1);
    },

    async sendQuestion() {
      if (!this.canSendMessage || !this.selectedChat) return;

      this.sendingMessage = true;

      try {
        // Create FormData
        const formData = new FormData();

        // Get phone number from selected chat
        const phoneNumber =
          this.selectedChat.phoneNumber || this.selectedChat.callerId;

        // Prepend "picture" if image generation is enabled
        const questionBody = this.generateImage
          ? `picture ${this.newQuestionText}`
          : this.newQuestionText;

        // Add form fields
        formData.append("From", phoneNumber);
        formData.append("To", "+19082018287"); // Your Twilio number
        formData.append("MessageSid", "web_" + Date.now()); // Generate a unique ID
        formData.append("Body", questionBody);
        formData.append("Type", "WEB");

        // Add model selection as a parameter
        formData.append("Model", this.selectedModel);

        // Add history count
        formData.append("HistoryCount", this.historyCount.toString());

        // Upload images if any
        if (this.uploadedImages.length > 0) {
          formData.append("NumMedia", this.uploadedImages.length.toString());

          // Upload each image first
          for (let i = 0; i < this.uploadedImages.length; i++) {
            const imageFormData = new FormData();
            imageFormData.append("file", this.uploadedImages[i].file);

            const uploadResponse = await this.$http.post(
              "https://broadcastmessageswercber.azurewebsites.net/api/home/GetImageUrl",
              imageFormData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            formData.append(`MediaUrl${i}`, uploadResponse.data);
          }
        }

        // Send the question
        const apiUrl = window.location.origin.startsWith("http://localhost")
          ? "https://localhost:5001/api/openai/AskAiTwilio"
          : "https://wercbergerproductions-app-164903695581.us-east1.run.app/api/openai/AskAiTwilio";

        const response = await this.$http.post(apiUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Clear inputs
        this.newQuestionText = "";
        this.uploadedImages = [];

        // Add the returned question object to messages
        if (response.data) {
          // Normalize the response to match OData casing (PascalCase)
          const normalizedMessage = {
            Id: response.data.id,
            Question: response.data.question,
            QuestionRecording: response.data.questionRecording,
            QuestionUrls: response.data.questionUrls,
            Answer: response.data.answer,
            Status: response.data.status,
            CallUuid: response.data.callUuid,
            CallerId: response.data.callerId,
            Type: response.data.type,
            Name: response.data.name,
            Date: response.data.date,
            Urls: response.data.urls,
            Provider: response.data.provider,
            Error: response.data.error,
            Cost: response.data.cost,
            ApiCost: response.data.apiCost,
            QuestionCost: response.data.questionCost,
            HistoryCount: response.data.historyCount,
          };

          this.messages.push(normalizedMessage);

          // Update sidebar
          await this.loadSidebar();

          // Scroll to bottom to show the new message
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("Error sending question:", error);
        alert("Failed to send question. Please try again.");
      } finally {
        this.sendingMessage = false;
      }
    },

    getDomain() {
      return window.location.origin.startsWith("http://localhost")
        ? "https://localhost:5001"
        : "https://wercbergerproductions-app-164903695581.us-east1.run.app";
    },
  },

  computed: {
    canSendMessage() {
      return (
        (this.newQuestionText.trim().length > 0 ||
          this.uploadedImages.length > 0) &&
        !this.sendingMessage &&
        this.selectedChat &&
        this.isCurrentUserChat
      );
    },

    isCurrentUserChat() {
      if (!this.selectedChat) return false;
      const currentUserPhone = this.$store.state.phoneNumber;
      return (
        this.selectedChat.phoneNumber === currentUserPhone ||
        this.selectedChat.callerId === currentUserPhone
      );
    },

    canManageAiProfile() {
      if (!this.selectedChat) return false;
      const lookup = this.getSelectedChatContextLookup();
      return (
        (lookup.userContactId !== null && lookup.userContactId !== undefined) ||
        !!lookup.callerId
      );
    },

    canManageWallet() {
      if (!this.selectedChat) return false;
      const lookup = this.getSelectedChatWalletLookup();
      return (
        (lookup.userId !== null && lookup.userId !== undefined) || !!lookup.callerId
      );
    },

    canSubmitPayment() {
      return (
        this.canManageWallet &&
        !this.addingPayment &&
        Number(this.paymentAmount) > 0 &&
        this.paymentMethod &&
        this.paymentMethod.trim().length > 0
      );
    },

    aiProfilePreview() {
      if (!this.aiProfileText) {
        return "No profile context set for this contact.";
      }
      return this.aiProfileText;
    },
  },

  watch: {
    selectedChat() {
      this.resetAiProfileEditor();
      this.aiProfileDialog = false;
      this.paymentsDialog = false;
      this.paymentLogs = [];
      this.paymentsError = "";
      this.paymentAmount = null;
      this.paymentNotes = "";
    },
    selectedModel(newModel) {
      localStorage.setItem("aiModel", newModel);
    },
    historyCount(newVal) {
      if (newVal > 30) {
        this.historyCount = 30;
        return;
      }
      if (newVal < 1) {
        this.historyCount = 1;
        return;
      }
      localStorage.setItem("historyCount", newVal.toString());
    },
  },
};
</script>
<style scoped>
/* RTL support for Hebrew text */
.rtl-text.markdown-content,
.rtl-text .markdown-content {
  direction: rtl;
  text-align: right;
}

.markdown-content >>> h1,
.markdown-content >>> h2,
.markdown-content >>> h3,
.markdown-content >>> h4,
.markdown-content >>> h5,
.markdown-content >>> h6 {
  font-weight: 600 !important;
  color: #1a1a1a !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
  margin-top: 28px !important;
  margin-bottom: 14px !important;
}

.markdown-content >>> h1:first-child,
.markdown-content >>> h2:first-child,
.markdown-content >>> h3:first-child {
  margin-top: 0 !important;
}

.markdown-content >>> h1 {
  font-size: 28px !important;
}

.markdown-content >>> h2 {
  font-size: 24px !important;
}

.markdown-content >>> h3 {
  font-size: 20px !important;
}

.markdown-content >>> h4 {
  font-size: 18px !important;
}

.markdown-content >>> strong {
  font-weight: 600 !important;
  color: #1a1a1a !important;
  font-size: inherit !important;
}

.markdown-content >>> b {
  font-weight: 600 !important;
  color: #1a1a1a !important;
  font-size: inherit !important;
}

.chat-page-wrapper {
  width: 100%;
  height: 100vh;
  height: -webkit-fill-available;
  overflow: hidden;
  margin-bottom: -100px;
}

.chat-container {
  display: flex;
  width: 100%;
  height: 100%;
  background-color: #f0f2f5;
  overflow: hidden;
}

/* Sidebar Styles */
.chat-sidebar {
  width: 400px;
  min-width: 400px;
  background-color: #fff;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  padding: 20px 16px 16px;
  color: white;
}

.sidebar-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.sidebar-actions {
  display: flex;
  gap: 4px;
}

.ask-ai-btn {
  margin-right: 4px;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 500;
  white-space: nowrap;
}

.last-updated {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

.refresh-btn {
  margin-left: 8px;
}

.sidebar-search {
  background-color: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px;
}

.sidebar-search >>> .v-input__control {
  min-height: 42px !important;
}

.sidebar-search >>> input {
  font-size: 14px;
}

.sidebar-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

.chat-item {
  display: flex;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.chat-item:hover {
  background-color: #f5f5f5;
}

.chat-item.active {
  background-color: #e8f5e9;
}

.chat-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-letter {
  color: white;
  font-size: 20px;
  font-weight: 500;
  user-select: none;
}

.chat-avatar-large {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-letter-large {
  color: white;
  font-size: 20px;
  font-weight: 500;
  user-select: none;
}

.chat-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.chat-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.chat-name {
  font-weight: 500;
  font-size: 16px;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-date {
  font-size: 12px;
  color: #667781;
  flex-shrink: 0;
  margin-left: 8px;
}

.chat-preview {
  font-size: 14px;
  color: #667781;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #667781;
}

/* Chat Area Styles */
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #efeae2;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(0, 0, 0, 0.03) 10px,
    rgba(0, 0, 0, 0.03) 20px
  );
  position: relative;
  min-height: 0;
  overflow: hidden;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex: 1;
  width: 100%;
}

.chat-header {
  background-color: #f0f0f0;
  padding: 12px 16px;
  border-bottom: 1px solid #d1d7db;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-header-info {
  display: flex;
  align-items: center;
}

.chat-header-name {
  font-size: 16px;
  font-weight: 500;
  color: #000;
}

.chat-header-phone {
  font-size: 13px;
  color: #667781;
}

.chat-header-wallet {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-top: 2px;
}

.chat-wallet-updated {
  color: #667781;
}

.chat-header-actions {
  margin-left: 12px;
  display: flex;
  align-items: center;
}

.chat-context-btn {
  text-transform: none !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #2e7d32 !important;
  border-radius: 20px;
  min-height: 34px !important;
  padding: 0 22px !important;
  border: 1px solid #2e7d32 !important;
  background: #ffffff !important;
  box-shadow: 0 2px 6px rgba(46, 125, 50, 0.16);
}

.chat-context-btn:hover {
  background: #e8f5e9 !important;
  box-shadow: 0 4px 9px rgba(46, 125, 50, 0.22);
}

.chat-context-btn.v-btn--disabled {
  background: #f5f5f5 !important;
  color: #b0b0b0 !important;
  border-color: #d5d5d5 !important;
  box-shadow: none !important;
}

.chat-payments-btn {
  text-transform: none !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #3949ab !important;
  border-radius: 20px;
  min-height: 34px !important;
  padding: 0 18px !important;
  border: 1px solid #3949ab !important;
  background: #ffffff !important;
}

.chat-payments-btn:hover {
  background: #e8eaf6 !important;
}

.chat-payments-btn.v-btn--disabled {
  background: #f5f5f5 !important;
  color: #b0b0b0 !important;
  border-color: #d5d5d5 !important;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.message-wrapper {
  margin-bottom: 12px;
}

.message-row {
  display: flex;
  margin-bottom: 4px;
}

.user-message {
  justify-content: flex-end;
}

.ai-message {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 85%;
  padding: 12px 16px 12px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  position: relative;
  display: flex;
  flex-direction: column;
}

.message-bubble > *:first-child {
  margin-top: 0 !important;
}

.user-bubble {
  background-color: #e1f5d5;
  border-bottom-right-radius: 2px;
}

.ai-bubble {
  background-color: #f8f8f8;
  border-bottom-left-radius: 2px;
}

.error-bubble {
  background-color: #ffebee;
  border-left: 3px solid #f44336;
}

.error-badge {
  display: inline-flex;
  align-items: center;
  background-color: #f44336;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  margin-bottom: 6px;
  font-weight: 500;
}

.pending-bubble {
  background-color: #fff8e1;
  border-left: 3px solid #ffa726;
}

.pending-badge {
  display: inline-flex;
  align-items: center;
  background-color: #ffa726;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  margin-bottom: 6px;
  font-weight: 500;
}

.pending-text {
  color: #999;
  font-style: italic;
}

.message-text {
  font-size: 16.5px;
  line-height: 1.8;
  color: #333;
  white-space: pre-line;
  word-wrap: break-word;
  margin: 0;
}

.markdown-content {
  white-space: normal;
  font-size: 16.5px;
  line-height: 1.8;
  color: #333;
  letter-spacing: 0.01em;
}

.markdown-content >>> p {
  margin: 0 0 18px 0;
  line-height: 1.8;
  font-size: 16.5px;
  color: #333;
}

.markdown-content >>> p:last-child {
  margin-bottom: 0;
}

.markdown-content >>> p:first-child {
  margin-top: 0;
}

.markdown-content >>> em,
.markdown-content >>> i {
  font-style: italic;
  color: #333;
}

.markdown-content >>> code {
  background-color: #f0f0f0;
  padding: 3px 6px;
  border-radius: 3px;
  font-family: "Roboto Mono", "Courier New", Consolas, monospace;
  font-size: 15px;
  color: #333;
  border: 1px solid #ddd;
  font-weight: 400;
}

.markdown-content >>> a {
  color: #1a73e8;
  text-decoration: none;
  transition: all 0.2s;
  font-weight: 400;
  font-size: inherit;
}

.markdown-content >>> a:hover {
  text-decoration: underline;
}

/* Lists */
.markdown-content >>> ul,
.markdown-content >>> ol {
  margin: 18px 0;
  padding-left: 28px;
}

.rtl-text.markdown-content >>> ul,
.rtl-text.markdown-content >>> ol {
  padding-left: 0;
  padding-right: 28px;
}

.markdown-content >>> ul {
  list-style-type: disc;
}

.markdown-content >>> ol {
  list-style-type: decimal;
}

.markdown-content >>> li {
  margin: 8px 0;
  line-height: 1.8;
  font-size: 16.5px;
  color: #333;
}

.markdown-content >>> li strong,
.markdown-content >>> li b {
  display: inline;
  margin-bottom: 0;
}

/* Blockquotes */
.markdown-content >>> blockquote {
  border-left: 3px solid #ddd;
  padding-left: 16px;
  margin: 18px 0;
  color: #555;
  font-style: italic;
  line-height: 1.8;
}

.rtl-text.markdown-content >>> blockquote {
  border-left: none;
  border-right: 4px solid #e0e0e0;
  padding-left: 0;
  padding-right: 16px;
}

/* Tables */
.markdown-content >>> table {
  border-collapse: collapse;
  margin: 16px 0;
  width: 100%;
}

.markdown-content >>> table th,
.markdown-content >>> table td {
  border: 1px solid #e0e0e0;
  padding: 8px 12px;
  text-align: left;
}

.rtl-text.markdown-content >>> table th,
.rtl-text.markdown-content >>> table td {
  text-align: right;
}

.markdown-content >>> table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

/* Horizontal rule */
.markdown-content >>> hr {
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 24px 0;
}

/* Pre and code blocks */
.markdown-content >>> pre {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 14px;
  overflow-x: auto;
  margin: 18px 0;
  line-height: 1.6;
}

.markdown-content >>> pre code {
  background-color: transparent;
  border: none;
  padding: 0;
  font-size: 14.5px;
}

/* RTL text support - applied to message bubbles */
.rtl-text {
  direction: rtl;
  text-align: right;
}

.rtl-text * {
  direction: rtl;
}

.message-images {
  margin-bottom: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.image-container {
  position: relative;
  display: inline-block;
}

.message-image {
  max-width: 280px;
  max-height: 280px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  display: block;
}

.ai-message .message-image {
  max-width: 500px;
  max-height: 500px;
}

.message-image:hover {
  transform: scale(1.02);
}

.image-download-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  color: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.2s;
}

.image-container:hover .image-download-btn {
  opacity: 1;
}

.image-modal-content {
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: calc(80vh - 70px);
  overflow: auto;
}

.modal-image {
  max-width: 100%;
  max-height: calc(80vh - 70px);
  width: auto;
  height: auto;
  object-fit: contain;
}

.message-audio {
  width: 100%;
  max-width: 300px;
  height: 40px;
  margin-bottom: 4px;
}

.message-time {
  font-size: 11px;
  color: #667781;
  margin-top: 2px;
  text-align: right;
  padding-left: 24px;
}

.message-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2px;
  gap: 8px;
  min-height: 16px;
}

.message-footer-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.message-footer .message-time {
  padding-left: 24px;
}

.message-history-count {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
  color: #90a4ae;
  background: rgba(144, 164, 174, 0.12);
  padding: 1px 6px 1px 4px;
  border-radius: 8px;
  border: 1px solid rgba(144, 164, 174, 0.25);
}

.message-footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-provider {
  font-size: 10px;
  color: #667781;
  font-style: italic;
}

.message-cost {
  font-size: 10px;
  color: #4caf50;
  font-weight: 600;
  background-color: rgba(76, 175, 80, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.message-cost-api {
  color: #667781;
  font-weight: 500;
}

.message-type-icon {
  position: absolute;
  bottom: 2px;
  opacity: 1;
  font-size: 12px !important;
  z-index: 1;
}

.rtl-text .message-type-icon {
  right: 6px;
  left: auto;
}

.message-bubble:not(.rtl-text) .message-type-icon {
  left: 6px;
}

.message-input-wrapper {
  background-color: transparent;
  padding: 8px 40px 12px;
  flex-shrink: 0;
}

.message-input-container {
  max-width: 1000px;
  margin: 0 auto;
}

.input-box {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;
}

.input-box:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.input-box:focus-within {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.uploaded-images-preview {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  padding: 0 8px;
}

.preview-image-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #e0e0e0;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-image-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.7) !important;
  color: white !important;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 8px 6px 4px;
}

.attach-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  color: #5f6368 !important;
  margin-left: 4px;
}

.attach-btn:hover {
  background-color: #f1f3f4 !important;
}

.model-select {
  width: 70px;
  min-width: 70px;
  max-width: 70px;
  flex-shrink: 0;
  order: 3;
}

.model-select >>> .v-input__control {
  min-height: 36px !important;
}

.model-select >>> .v-input__slot {
  padding: 0 4px !important;
  box-shadow: none !important;
}

.model-select >>> .v-select__selection {
  font-size: 13px;
  color: #5f6368;
  font-weight: 500;
  margin: 0 !important;
}

.model-select >>> .v-select__selections {
  padding: 0 !important;
  align-items: center !important;
}

.model-select-text {
  display: flex;
  align-items: center;
  height: 36px;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1;
  color: #5f6368;
  font-weight: 500;
  transform: translateY(3px);
}

.history-count-input {
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  flex-shrink: 0;
}

.history-count-input >>> .v-input__control {
  min-height: 36px !important;
}

.history-count-input >>> .v-input__slot {
  padding: 0 4px !important;
  min-height: 36px !important;
  height: 36px !important;
  border: 1.5px solid #bdbdbd !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;
}

.history-count-input:hover >>> .v-input__slot {
  border-color: #5f6368 !important;
}

.history-count-input >>> .v-text-field__slot {
  display: flex !important;
  align-items: center !important;
  height: 100% !important;
}

.history-count-input >>> input {
  font-size: 13px;
  color: #5f6368;
  font-weight: 500;
  text-align: center;
  padding: 0 !important;
  margin: 0 !important;
  height: auto !important;
  align-self: center !important;
  appearance: textfield;
  -moz-appearance: textfield;
}

.history-count-input >>> input::-webkit-outer-spin-button,
.history-count-input >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.message-input {
  flex: 1 1 auto;
  min-width: 0;
  background-color: transparent !important;
  order: 2;
}

.message-input >>> .v-input__control {
  min-height: 36px !important;
}

.message-input >>> .v-input__slot {
  padding: 0 8px !important;
  box-shadow: none !important;
}

.message-input >>> textarea {
  max-height: 120px;
  overflow-y: auto !important;
  font-size: 16px;
  line-height: 1.5;
  color: #202124;
  padding: 2px 0;
}

.message-input >>> textarea::placeholder {
  color: #5f6368;
  opacity: 1;
}

.image-gen-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  order: 1;
  color: #5f6368 !important;
  transition: all 0.2s;
}

.image-gen-btn:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

.image-gen-btn.active {
  color: #2196f3 !important;
  background-color: rgba(33, 150, 243, 0.1) !important;
}

.send-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  order: 4;
  color: #fff !important;
}

.send-btn:disabled {
  color: #dadce0 !important;
}

.send-btn:hover:not(:disabled) {
  background-color: #999 !important;
}

.empty-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #667781;
}

.loading-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #667781;
  font-size: 16px;
}

/* Scrollbar Styling */
.sidebar-list::-webkit-scrollbar,
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.sidebar-list::-webkit-scrollbar-track,
.messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-list::-webkit-scrollbar-thumb,
.messages-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.sidebar-list::-webkit-scrollbar-thumb:hover,
.messages-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Back button: hidden on desktop, shown on tablet/phone */
.back-btn {
  display: none !important;
}

/* Responsive */

/* Large tablets / small desktop (≤1024px) */
@media (max-width: 1024px) {
  .chat-sidebar {
    width: 300px;
    min-width: 300px;
  }

  .message-bubble {
    max-width: 80%;
  }

  .message-input-wrapper {
    padding: 6px 20px 10px;
  }

  .btn-label {
    display: none;
  }

  .chat-payments-btn,
  .chat-context-btn {
    min-width: 36px !important;
    padding: 0 8px !important;
  }
}

/* iPad portrait and small tablets (≤820px) */
@media (max-width: 820px) {
  .chat-sidebar {
    width: 260px;
    min-width: 260px;
  }

  .message-input-wrapper {
    padding: 6px 12px 10px;
  }

  .chat-header {
    padding: 10px 12px;
  }

  .chat-header-wallet {
    display: none;
  }
}

/* iPad mini portrait and phones (≤768px): full-width sidebar with back button */
@media (max-width: 768px) {
  .chat-sidebar {
    width: 100%;
    min-width: 100%;
    position: absolute;
    z-index: 10;
    height: 100%;
  }

  .chat-sidebar.has-selection {
    display: none;
  }

  .back-btn {
    display: inline-flex !important;
  }

  .message-bubble {
    max-width: 85%;
  }

  .message-input-wrapper {
    padding: 6px 8px 10px;
  }

  .ai-message .message-image {
    max-width: 260px;
    max-height: 260px;
  }
}

/* Phones (≤600px) */
@media (max-width: 600px) {
  .chat-header-actions {
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
  }

  .chat-payments-btn,
  .chat-context-btn {
    margin: 0 !important;
  }
}

/* AI Images featured card */
.ai-images-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  cursor: pointer;
  background: linear-gradient(135deg, #f1f8f1 0%, #e8f5e9 100%);
  border-bottom: 2px solid #c8e6c9;
  transition: background-color 0.2s;
}

.ai-images-card:hover {
  background: linear-gradient(135deg, #e8f5e9 0%, #dcedc8 100%);
}

.ai-images-icon-wrap {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: linear-gradient(135deg, #4caf50 0%, #43a047 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(76, 175, 80, 0.35);
}

.ai-images-card-info {
  flex: 1;
  min-width: 0;
}

.ai-images-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #1b5e20;
  line-height: 1.2;
}

.ai-images-card-sub {
  font-size: 12px;
  color: #558b2f;
  margin-top: 2px;
}

/* Context Button and Dialog */
.context-btn-wrapper {
  margin-top: 6px;
  margin-bottom: 4px;
}

.context-btn {
  text-transform: none !important;
  font-size: 12px;
  height: 24px !important;
  padding: 0 8px !important;
}

.context-text {
  font-family: "Roboto Mono", "Courier New", Consolas, monospace;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  color: #333;
  max-height: 60vh;
  overflow-y: auto;
}

.ai-profile-meta {
  font-size: 15px;
  color: #4a4a4a;
  line-height: 1.35;
  margin-bottom: 10px !important;
}

.ai-profile-meta strong {
  font-size: 16px;
  font-weight: 700;
}

.ai-profile-meta > div + div {
  margin-top: 3px;
}

.ai-profile-meta-divider {
  width: 100%;
  height: 1px;
  background-color: #e2e2e2;
  margin: 8px 0 12px;
}

.ai-profile-columns {
  display: flex;
  gap: 18px;
  align-items: stretch;
}

.ai-profile-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.ai-profile-editor >>> .v-input__slot {
  border: 1px solid #bdbdbd !important;
  border-radius: 6px !important;
  background-color: #fff !important;
}

.ai-profile-editor {
  flex: 1;
}

.ai-profile-editor >>> textarea {
  line-height: 1.65;
  font-size: 15px;
  white-space: pre-wrap;
}

.ai-profile-hint {
  font-size: 12px;
  color: #757575;
}

.ai-profile-preview-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a4a4a;
  margin-bottom: 6px;
}

.ai-profile-preview {
  flex: 1;
  font-family: "Roboto Mono", "Courier New", Consolas, monospace;
  font-size: 13px;
  line-height: 1.65;
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 14px;
  min-height: 320px;
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.ai-profile-cancel-btn {
  color: #999999 !important;
  border-color: #999999 !important;
  text-transform: none !important;
  font-weight: 500;
}

.wallet-summary-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 14px;
}

.payment-form-grid {
  display: grid;
  grid-template-columns: 1.2fr 1.6fr auto;
  gap: 10px;
  align-items: center;
}

.payment-logs-title {
  font-size: 14px;
  font-weight: 600;
  color: #3a3a3a;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.payment-logs-list {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  max-height: 360px;
  overflow-y: auto;
  background-color: #fafafa;
}

.payment-log-row {
  display: grid;
  grid-template-columns: 120px 1fr 170px;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #ececec;
}

.payment-log-row:last-child {
  border-bottom: none;
}

.payment-log-amount {
  font-size: 14px;
  font-weight: 700;
}

.payment-log-details {
  font-size: 13px;
  color: #424242;
  min-width: 0;
}

.payment-log-notes {
  font-size: 12px;
  color: #757575;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.payment-log-date {
  font-size: 12px;
  color: #616161;
  text-align: right;
}

@media (max-width: 1024px) {
  .ai-profile-columns {
    flex-direction: column;
  }

  .payment-form-grid {
    grid-template-columns: 1fr;
  }

  .payment-log-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .payment-log-date {
    text-align: left;
  }
}
</style>
